import React, { useContext, useRef } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BorderBottomIcon from '@mui/icons-material/BorderBottom';
import BorderLeftIcon from '@mui/icons-material/BorderLeft';
import BorderRightIcon from '@mui/icons-material/BorderRight';
import BorderTopIcon from '@mui/icons-material/BorderTop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridOffIcon from '@mui/icons-material/GridOff';
import GridOnIcon from '@mui/icons-material/GridOn';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { LabelMarker } from '../model';
import LabelPreview from './LabelPreview';

// Label text has to be readable across a warehouse, so the editor needs real point sizes
// rather than Quill's small/large/huge. Keep in sync with LabelConfig::EDITOR_FONT_SIZES,
// which guards the default template against using a size the editor cannot offer.
const FONT_SIZES = ['16px', '24px', '36px', '48px', '60px', '96px'];

// Style attributors, not Quill's default class ones: the stored HTML is printed outside the
// editor, where the ql-* classes carry no styling. This is react-quill-new's Quill 2, a
// separate registry from the react-quill (Quill 1) editors on the other config pages, so
// registering here changes nothing there.
const SizeStyle = Quill.import('attributors/style/size') as { whitelist: string[] };
SizeStyle.whitelist = FONT_SIZES;
Quill.register('formats/size', SizeStyle, true);
Quill.register('formats/align', Quill.import('attributors/style/align'), true);

// No list buttons: saved as raw HTML, a Quill 2 bullet list is <ol><li data-list="bullet">,
// which the print endpoint would render as a numbered list.
const QUILL_MODULES = {
  toolbar: [
    [{ size: FONT_SIZES }],
    ['bold', 'italic', 'underline'],
    [{ align: '' }, { align: 'center' }, { align: 'right' }],
    ['clean'],
  ],
  table: true,
};

// The slice of Quill 2's table module the buttons drive, typed here because quill is only a
// transitive dependency. getTable returns a null cell when the cursor is not in a table.
interface TableModule {
  getTable: (range?: { index: number; length: number }) => [unknown, unknown, unknown, number];
  insertTable: (rows: number, columns: number) => void;
  insertRowAbove: () => void;
  insertRowBelow: () => void;
  insertColumnLeft: () => void;
  insertColumnRight: () => void;
  deleteRow: () => void;
  deleteColumn: () => void;
  deleteTable: () => void;
}

interface TableButton {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

// Clicking a button would otherwise blur the editor, and the table module reads the live
// selection to find its cell.
const keepEditorFocus = (event: React.MouseEvent) => event.preventDefault();

// The size picker renders its options blank unless CSS supplies a label, and snow only ships
// labels for the small/large/huge whitelist it replaces. Cell borders are an on-screen guide
// only; nothing about them is stored, so the label prints borderless.
const editorSx = {
  mb: 2,
  '& .ql-container': { minHeight: 320, maxHeight: 520, overflowY: 'auto' },
  '& .ql-size .ql-picker-item::before, & .ql-size .ql-picker-label::before': {
    content: 'attr(data-value)',
  },
  '& .ql-size .ql-picker-label:not([data-value])::before': { content: '"Size"' },
  '& .ql-size': { width: 72 },
  '& .ql-editor td': { border: '1px dashed #bdbdbd' },
} as const;

interface LabelTemplateEditorProps {
  body: string;
  markers: LabelMarker[];
  onBodyChange: (value: string, source: string) => void;
}

const LabelTemplateEditor: React.FC<LabelTemplateEditorProps> = ({ body, markers, onBodyChange }) => {
  const { showErrorSnackBar } = useContext(SnackBarContext);
  const quillRef = useRef<ReactQuill>(null);

  // Copy-and-paste is awkward inside a rich editor, so a marker chip drops its text straight
  // at the cursor. With no cursor (the editor was never focused) it goes to the end.
  const handleInsertMarker = (marker: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      showErrorSnackBar?.('Click into the template first, then choose a variable.');
      return;
    }
    const index = editor.getSelection(true)?.index ?? editor.getLength() - 1;
    editor.insertText(index, marker, 'user');
    editor.setSelection(index + marker.length, 0);
  };

  const handleInsertTable = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const table = editor.getModule('table') as TableModule;
    const range = editor.getSelection(true);
    const [, , cell] = table.getTable(range);
    if (cell) {
      showErrorSnackBar?.('A table cannot go inside another table.');
      return;
    }
    // Quill's insertTable turns the text before the cursor into the first cell, so a
    // mid-line cursor splits the line first and the table lands between the two halves.
    const [, offset] = editor.getLine(range.index);
    if (offset > 0) {
      editor.insertText(range.index, '\n', 'user');
      editor.setSelection(range.index + 1, 0, 'silent');
    }
    table.insertTable(2, 2);
  };

  const handleCellAction = (run: (table: TableModule) => void) => {
    const table = quillRef.current?.getEditor().getModule('table') as TableModule | undefined;
    const cell = table?.getTable()[2];
    if (!table || !cell) {
      showErrorSnackBar?.('Click inside a table first.');
      return;
    }
    run(table);
  };

  // Grouped by what they change. Every action but Insert table acts on the cell holding the
  // cursor; deleting the last row or column removes the table.
  const tableButtonGroups: TableButton[][] = [
    [
      { label: 'Insert table', icon: <GridOnIcon fontSize="small" />, onClick: handleInsertTable },
      {
        label: 'Delete table',
        icon: <GridOffIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.deleteTable()),
      },
    ],
    [
      {
        label: 'Insert row above',
        icon: <BorderTopIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.insertRowAbove()),
      },
      {
        label: 'Insert row below',
        icon: <BorderBottomIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.insertRowBelow()),
      },
      {
        label: 'Delete row',
        icon: <DeleteOutlineIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.deleteRow()),
      },
    ],
    [
      {
        label: 'Insert column left',
        icon: <BorderLeftIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.insertColumnLeft()),
      },
      {
        label: 'Insert column right',
        icon: <BorderRightIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.insertColumnRight()),
      },
      {
        label: 'Delete column',
        icon: <DeleteOutlineIcon fontSize="small" />,
        onClick: () => handleCellAction((table) => table.deleteColumn()),
      },
    ],
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Template variables — click to insert one where the cursor is
        </Typography>
        {markers.map((variable) => (
          <Tooltip key={variable.marker} title={variable.description}>
            <Chip
              icon={<AddIcon sx={{ fontSize: 14 }} />}
              label={variable.marker}
              onClick={() => handleInsertMarker(variable.marker)}
              clickable
              variant="outlined"
              size="small"
              sx={{ mr: 1, mb: 0.5 }}
            />
          </Tooltip>
        ))}
      </Box>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Label Template
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {tableButtonGroups.map((group) => (
          <Box
            key={group[0].label}
            sx={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}
          >
            {group.map((button) => (
              <Tooltip key={button.label} title={button.label}>
                <IconButton
                  size="small"
                  aria-label={button.label}
                  onMouseDown={keepEditorFocus}
                  onClick={button.onClick}
                >
                  {button.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        ))}
      </Box>
      {/* Side by side on wide screens; the preview wraps below the editor when space runs out. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ ...editorSx, flex: '1 1 420px', minWidth: 0 }}>
          <ReactQuill
            ref={quillRef}
            placeholder="Enter the label layout here..."
            theme="snow"
            value={body}
            useSemanticHTML={false}
            onChange={(value, _delta, source) => onBodyChange(value, source)}
            modules={QUILL_MODULES}
          />
        </Box>
        <LabelPreview body={body} />
      </Box>
    </Box>
  );
};

export default LabelTemplateEditor;
